import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import Footer from '@/components/shared/Footer';

export default function StilPage() {
  const nav = useNavigate();

  return (
    <div className="min-h-screen bg-bone" data-testid="stil-page">
      <div className="max-w-5xl mx-auto px-6 pt-8">
        <button onClick={() => nav('/')} className="flex items-center gap-2 text-foreground/60 hover:text-foreground font-body text-sm transition-colors">
          <ArrowLeft size={16} /> <span className="font-display text-xl font-semibold"><span className="text-terracotta">bel</span>osophie</span>
        </button>
      </div>

      {/* Hero Image */}
      <section className="max-w-5xl mx-auto px-6 pb-4">
        <div className="rounded-2xl overflow-hidden shadow-xl">
          <img src="https://static.prod-images.emergentagent.com/jobs/e0acc782-1a22-4dc5-a2d2-0792ba0290c7/images/2aabefc8bec9c24cf3598f14062d67313dfcb4a70fbeacaaa352199ef79cf172.jpeg" alt="Frau mit Stil und Haltung – Ausdruck der Persönlichkeit" className="w-full h-[250px] md:h-[360px] object-cover object-center" />
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 pt-12 pb-8">
        <p className="font-body text-sm uppercase tracking-[0.2em] text-terracotta font-semibold mb-3">MEIN BLICK AUF</p>
        <h1 className="font-display text-4xl md:text-5xl font-medium tracking-tight leading-[1.1]">
          Stil, Vitalität und Lebensqualität
        </h1>
      </section>

      <section className="max-w-4xl mx-auto px-6 py-8">
        <div className="max-w-xl space-y-6">
          <div>
            <h2 className="font-display text-2xl font-medium mb-4">Stil beginnt nicht bei Kleidung.</h2>
            <p className="font-body text-foreground/60 leading-relaxed">
              Stil ist eine Haltung.<br />
              Wie begegne ich dem Leben?<br />
              Wie gehe ich mit mir selbst um?<br />
              Wie zeige ich mich nach aussen?
            </p>
          </div>
          <p className="font-body text-foreground/50 leading-relaxed">
            Für mich gehören viele Dinge zusammen:
          </p>
        </div>
      </section>

      {/* 3 Bereiche */}
      <section className="bg-warmgray/50">
        <div className="max-w-4xl mx-auto px-6 py-16">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="space-y-4">
              <h3 className="font-display text-2xl font-medium">Der Mensch</h3>
              <div className="space-y-2 font-body text-foreground/60 text-sm leading-relaxed">
                <p>Wer bin ich?</p>
                <p>Was macht mich aus?</p>
                <p>Was möchte ich leben?</p>
              </div>
            </div>
            <div className="space-y-4">
              <h3 className="font-display text-2xl font-medium">Der Körper</h3>
              <div className="space-y-2 font-body text-foreground/60 text-sm leading-relaxed">
                <p>Bewegung. Vitalität. Ernährung.</p>
                <p className="text-foreground/40 italic">Nicht als Zwang.</p>
                <p className="text-foreground/40 italic">Sondern als Wertschätzung für sich selbst.</p>
              </div>
            </div>
            <div className="space-y-4">
              <h3 className="font-display text-2xl font-medium">Der Ausdruck</h3>
              <div className="space-y-2 font-body text-foreground/60 text-sm leading-relaxed">
                <p>Kleidung. Farben. Auftreten. Das eigene Zuhause.</p>
                <p className="text-foreground/40 italic">Nicht um jemand anderes zu werden.</p>
                <p className="text-foreground/40 italic">Sondern als Ausdruck der eigenen Persönlichkeit.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Golf */}
      <section className="max-w-4xl mx-auto px-6 py-16">
        <div className="max-w-xl space-y-6">
          <p className="font-body text-foreground/50 leading-relaxed">
            Meine Leidenschaft für Bewegung, Natur und Golf begleitet mich dabei.
          </p>
          <div>
            <h3 className="font-display text-xl font-medium mb-4">Golf hat mich vieles gelehrt:</h3>
            <div className="space-y-3">
              {['Geduld.', 'Selbstreflexion.', 'Umgang mit Fehlern.', 'Gelassenheit.'].map((text, i) => (
                <p key={i} className="font-body text-foreground/60 pl-4 border-l-2 border-terracotta/30">{text}</p>
              ))}
            </div>
          </div>
          <div className="bg-gradient-to-r from-terracotta/5 to-amber-50/50 border-l-4 border-terracotta rounded-r-2xl p-6 mt-8">
            <p className="font-display text-xl font-medium text-foreground/70 italic leading-relaxed">
              Nicht jeder Schlag gelingt.<br />
              Aber jeder nächste Schlag zählt.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-warmgray/50">
        <div className="max-w-3xl mx-auto px-6 py-16 text-center">
          <Button onClick={() => window.location.href = 'mailto:info@belosophie.com?subject=Stil und Vitalität'} className="rounded-full bg-terracotta hover:bg-terracotta-dark text-white px-10 py-6 text-lg font-medium shadow-lg shadow-terracotta/20 hover:scale-[1.02] transition-all">
            Lernen wir uns kennen <ArrowRight size={20} className="ml-2" />
          </Button>
          <p className="font-body text-xs text-foreground/30 mt-4">info@belosophie.com</p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
